import { useEffect, useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../../_redux/user-actions/UserActionsAction";
import {
  copy,
  DeleteButton,
  ExportButton,
  exportToExcel,
  exportToPDF,
  FormButton,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../../_metronic/_partials/controls";
import { ParentUsersFilter } from "./parent-users-filter/ParentUsersFilter";
import { ParentUsersTable } from "./parent-users-table/ParentUsersTable";
import { useParentUsersUIContext } from "./ParentUsersUIContext";

export function ParentUsersCard() {
  // Parent Users Card UI Context
  const parentUsersUIContext = useParentUsersUIContext();
  const parentUsersUIProps = useMemo(() => {
    return {
      ids: parentUsersUIContext.ids,
      setIds: parentUsersUIContext.setIds,
      queryParams: parentUsersUIContext.queryParams,
      openIPListEditModal: parentUsersUIContext.openIPListEditModal
    };
  }, [parentUsersUIContext]);

  // Getting curret state of UserActions from store (Redux)
  const { currentState, currentActiveTerm } = useSelector(
    (state) => ({
      currentState: state.userActions,
      currentActiveTerm: state.auth.user.currentActiveTerm
    }),
    shallowEqual
  );  
  const { actionsLoading, isSuccess, type, entities, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isEditionAuthorized } = getAuthorities(authorities);

  // Parent Users Redux state
  const dispatch = useDispatch();

  const [userIds, setUserIds] = useState();

  useEffect(() => {
    if (entities) {
      const selectedEntities = entities.filter(entity => parentUsersUIProps.ids.includes(entity.id));
      const userIds = [...new Set(selectedEntities.map(entity => entity.userId))];
      setUserIds(userIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentUsersUIProps.ids]);
    
  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Veli Kullanıcı Listesini Excele Aktarır */
  const exportParentUsersToExcel = () => {
    dispatch(actions.getAllParentUsers(parentUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
        const rows = [];
        const widths = [30, 30, 30, 30, 30, 30, 15];
        const columns = ["TC Kimlik No", "Adı", "Soyadı", "Öğrenci", "Şube", "Kullanıcı Grubu", "Aktif mi?"];
          
        entities.forEach(entity => rows.push([
          entity.tcKimlikNo,
          entity.name,
          entity.surname,
          entity.studentName,
          entity.section,
          entity.userGroups,
          entity.isActive ? "Aktif" : "Pasif"
        ]));
          
        const filename = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Veli Kullanıcı Listesi";
        const sheetname = "Veli Kullanıcı Listesi";
        exportToExcel({ columns, rows, widths, filename, sheetname });
      });
  }
  
  /** Belli Bir Veli Kullanıcı Listesini PDF e Aktarır */
  const exportParentUsersToPDF = () => {
    dispatch(actions.getAllParentUsers(parentUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "TC Kimlik No",
          rows: entities.map(entity => entity.tcKimlikNo)
        },
        {
          header: "Adı",
          rows: entities.map(entity => entity.name)
        },
        {
          header: "Soyadı",
          rows: entities.map(entity => entity.surname)
        },
        {
          header: "Öğrenci",
          rows: entities.map(entity => entity.studentName)
        },
        {
          header: "Şube",
          rows: entities.map(entity => entity.section)
        },
        {
          header: "Kullanıcı Grubu",
          rows: entities.map(entity => entity.userGroups)
        },
        {
          header: "Aktif mi?",
          rows: entities.map(entity => entity.isActive ? "Aktif" : "Pasif")
        }
      ];
      
      const filename = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Veli Kullanıcı Listesi";
      exportToPDF(views, filename);
    });
  }
    
  /** Belli Bir Veli Kullanıcı Listesini Yazdırma Penceresini Açar */
  const printParentUsers = () => {
    dispatch(actions.getAllParentUsers(parentUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Veli Kullanıcı Listesi";
      const properties = ["TC Kimlik No", "Adı", "Soyadı", "Öğrenci", "Şube", "Kullanıcı Grubu", "Aktif mi?"];
      
      entities.forEach(entity => {
        data.push({
          "TC Kimlik No": entity.tcKimlikNo,
          "Adı": entity.name,
          "Soyadı": entity.surname,
          "Öğrenci": entity.studentName,
          "Şube": entity.section,
          "Kullanıcı Grubu": entity.userGroups,
          "Aktif mi?": entity.isActive ? "Aktif" : "Pasif"
        });
      });
      
      print({ data, properties, header });
    });
  }

  /** Belli Bir Veli Kullanıcı Listesini Panoya Kopyalar */
  const copyParentUsers = () => {
    dispatch(actions.getAllParentUsers(parentUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["TC Kimlik No", "Adı", "Soyadı", "Öğrenci", "Şube", "Kullanıcı Grubu", "Aktif mi?"];
      const header = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Veli Kullanıcı Listesi";
      entities.forEach(entity => rows.push([
        entity.tcKimlikNo,
        entity.name,
        entity.surname,
        entity.studentName,
        entity.section,
        entity.userGroups,
        entity.isActive ? "Aktif" : "Pasif"
      ]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Kullanıcı Listesinin Aktiflik Durumunu Değiştirir */
  const toggleActiveness = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
      
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }
    dispatch(actions.toggleActiveness(ids)).then(result => onResult(result));
  }
      
    const onResult = ({ isSuccess, type, message }) => {
      resultAlert(type, message);
        
      if (isSuccess) {
        setTimeout(() => {
          parentUsersUIProps.setIds([]);
          dispatch(actions.cleanUserActionsState());
          dispatch(actions.getParentUsers(parentUsersUIProps.queryParams));
        }, 2500);
      }
    };

  const title = <>Veli Kullanıcı Listesi{parentUsersUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{parentUsersUIProps.ids.length}</span>]</> :
                   ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isEditionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportParentUsersToExcel}
                exportToPDF={exportParentUsersToPDF}
                print={printParentUsers}
                copy={copyParentUsers}
              />
            }
            {
              isEditionAuthorized &&
              <>
                <FormButton
                  path="Communication/Write"
                  title="IP Listesi Düzenle"
                  method={() => parentUsersUIProps.openIPListEditModal(userIds)}
                />
                <DeleteButton
                  path="General/Settings-1"
                  title="Aktif Pasif Yap"
                  method={() => toggleActiveness(userIds)}
                />
              </>
            }
          </CardHeaderToolbar>
        }
      </CardHeader>
      <CardBody>
        <ParentUsersFilter />
        <ParentUsersTable
          toggleActiveness={toggleActiveness}
        />
      </CardBody>
    </Card>
  );
}